import React, { useMemo } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useLocation,
  useSearchParams as useReactRouterSearchParams,
  useParams as useReactRouterParams,
} from 'react-router-dom';

export { useNavigate, useLocation } from 'react-router-dom';

// 1. useRouter shim for Vite SPA
export function useRouter() {
  const navigate = useNavigate();
  // Real Next.js `useRouter()` returns a stable object across renders. Callers
  // (e.g. useProactiveLessonPersonalization) put `router` in effect dependency
  // arrays, so a fresh object literal here re-fires those effects on every
  // render — turning into an infinite trigger-generation loop. Memoize on
  // `navigate`, which react-router-dom already guarantees is stable.
  return useMemo(
    () => ({
      push: (url: string, _opts?: any) => navigate(url),
      replace: (url: string, _opts?: any) => navigate(url, { replace: true }),
      back: () => navigate(-1),
      refresh: () => window.location.reload(),
      prefetch: () => {},
    }),
    [navigate],
  );
}

// 2. usePathname shim
export function usePathname() {
  const location = useLocation();
  return location.pathname;
}

// 3. useSearchParams shim (returns URLSearchParams object like Next.js)
export function useSearchParams() {
  const [searchParams] = useReactRouterSearchParams();
  return searchParams;
}

// 4. useParams shim
export function useParams<T extends Record<string, string | undefined> = Record<string, string>>() {
  return useReactRouterParams() as T;
}

// 5. Link shim (accepts both href and to props)
export function Link({ href, to, onClick, children, ...props }: any) {
  const destination = to || href || '#';
  const isExternal =
    typeof destination === 'string' &&
    (destination.startsWith('http://') ||
      destination.startsWith('https://') ||
      destination.startsWith('mailto:') ||
      destination.startsWith('tel:'));

  if (isExternal) {
    return (
      <a href={destination} onClick={onClick} {...props}>
        {children}
      </a>
    );
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) onClick(e);
    if (e.defaultPrevented) return;
    if (typeof destination === 'string' && destination.includes('#')) {
      const hashIndex = destination.indexOf('#');
      const targetPath = destination.slice(0, hashIndex);
      const hash = destination.slice(hashIndex + 1);
      if (hash && (!targetPath || targetPath === window.location.pathname)) {
        const el = document.getElementById(hash);
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth' });
          }, 0);
        }
      }
    }
  };

  return (
    <RouterLink to={destination} onClick={handleClick} {...props}>
      {children}
    </RouterLink>
  );
}

// 6. Image shim
export function Image({
  src,
  alt,
  width,
  height,
  className,
  style,
  priority: _priority,
  fill,
  unoptimized: _unoptimized,
  quality: _quality,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  ...props
}: any) {
  const mergedStyle = fill
    ? {
        position: 'absolute' as const,
        height: '100%',
        width: '100%',
        inset: 0,
        ...style,
      }
    : style;

  return (
    <img
      src={typeof src === 'object' && src?.src ? src.src : src}
      alt={alt || ''}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      className={className}
      style={mergedStyle}
      {...props}
    />
  );
}

// 7. dynamic shim for Next.js dynamic imports
export function dynamic(importFn: () => Promise<any>, options?: any) {
  // `next/dynamic` accepts loaders that resolve to either a module namespace
  // (`{ default: Component }`) or a plain component reference, e.g.
  // `import('./x').then((m) => m.NamedExport)`. `React.lazy` only accepts the
  // former, so normalize the latter here.
  const normalizedImportFn = () =>
    importFn().then((resolved) =>
      resolved && typeof resolved === 'object' && 'default' in resolved
        ? resolved
        : { default: resolved },
    );
  const LazyComponent = React.lazy(normalizedImportFn);
  return function DynamicWrapper(props: any) {
    const Fallback = options?.loading || (() => null);
    return (
      <React.Suspense fallback={<Fallback />}>
        <LazyComponent {...props} />
      </React.Suspense>
    );
  };
}

export function notFound(): never {
  throw new Error('404 Page Not Found');
}

export function redirect(url: string): never {
  window.location.href = url;
  throw new Error(`Redirecting to ${url}`);
}

export default Image;
